<template>
  <d-container
    fluid
    class="main-content-container px-4">
    <!-- Page Header -->
    <d-row
      no-gutters
      class="page-header py-4">
      <d-col
        class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">
          {{ $t('shipmentsReports.reports') }}
        </span>
        <h3 class="page-title">
          {{ $t('shipmentsReports.reportsList') }}
        </h3>
      </d-col>
    </d-row>

    <d-card style="width: 100%;" class="p-4 mb-4">
      <d-row>
        <d-col cols="5">
          <label>
            {{ $t('report.dateRange') }}:
          </label>

          <date-range-picker
            ref="daily-report-date-picker"
            :locale-data="{
              'firstDay': 1,
              'format': 'DD-MM-YYYY',
              'daysOfWeek': [$t('ui.daysOfWeek.sun'), $t('ui.daysOfWeek.mon'), $t('ui.daysOfWeek.tue'), $t('ui.daysOfWeek.wed'), $t('ui.daysOfWeek.thur'), $t('ui.daysOfWeek.fri'), $t('ui.daysOfWeek.sat')],
              'monthNames': [$t('ui.monthNames.jan'), $t('ui.monthNames.feb'), $t('ui.monthNames.mar'), $t('ui.monthNames.apr'), $t('ui.monthNames.may'), $t('ui.monthNames.jun'), $t('ui.monthNames.jul'), $t('ui.monthNames.aug'), $t('ui.monthNames.sep'), $t('ui.monthNames.oct'), $t('ui.monthNames.nov'), $t('ui.monthNames.dec')],
            }"
            class="w-100"
            :singleDatePicker="false"
            :timePicker="false"
            :showWeekNumbers="false"
            :showDropdowns="false"
            :autoApply="true"
            v-model="generator.date_range"
            :linkedCalendars="false">
            <template v-slot:input="picker" style="min-width: 350px;">
              {{ formatDateStart(picker.startDate) }} - {{ formatDateStart(picker.endDate) }}
            </template>
          </date-range-picker>
        </d-col>

        <d-col cols="4">
          <label class="w-100">
            {{ $t('shipmentsReports.type') }}:
          </label>
          <d-button-group>
            <d-button
              :outline="generator.type !== 'ALL'"
              @click.prevent="generator.type = 'ALL'">
              {{ $t('shipmentsReports.typeAll') }}
            </d-button>
            <d-button
              :outline="generator.type !== 'WITHOUT_REPORT'"
              @click.prevent="generator.type = 'WITHOUT_REPORT'">
              {{ $t('shipmentsReports.typeWithoutReport') }}
            </d-button>
          </d-button-group>
        </d-col>

        <d-col cols="3">
          <label class="w-100"></label>
          <d-button
            theme="primary"
            class="w-100 mt-2"
            @click.prevent="generateReport">
            <template v-if="!generatingReportInProgress">
              {{ $t('report.generateReport') }}
            </template>
            <template v-else>
              <div class="spinner-border spinner-border-sm" role="status"></div>
            </template>
          </d-button>
        </d-col>
      </d-row>
    </d-card>

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th scope="col" class="border-0" style="min-width: 200px;">{{ $t('shipmentsReports.status') }}</th>
            <th scope="col" class="border-0">{{ $t('shipmentsReports.dateRange') }}</th>
            <th scope="col" class="border-0" style="min-width: 100px;">{{ $t('shipmentsReports.type') }}</th>
            <th scope="col" class="border-0" style="min-width: 80px;">{{ $t('shipmentsReports.amount') }}</th>
            <th scope="col" class="border-0" style="min-width: 180px">{{ $t('shipmentsReports.createdAt') }}</th>
            <th scope="col" class="border-0" style="min-width: 180px">{{ $t('shipmentsReports.lastChange') }}</th>
            <th scope="col" class="border-0" style="width: 140px;">{{ $t('ui.actions') }}</th>
          </tr>
        </thead>
        <tbody v-if="!loadError">
          <tr
            v-for="(report, index) of reports"
            :key="'report-' + index">
            <td class="align-middle">
              <d-badge
                :outline="true"
                :theme="statusBadgeTheme[report.status]">
                {{ $t('shipmentsReports.statuses.' + report.status) }}
              </d-badge>
            </td>
            <td class="align-middle">
              {{ report.date_from }} - {{ report.date_to }}
            </td>
            <td class="align-middle">
              <template v-if="report.type === 'ALL'">
                {{ $t('shipmentsReports.typeAll') }}
              </template>

              <template v-if="report.type === 'WITHOUT_REPORT'">
                {{ $t('shipmentsReports.typeWithoutReport') }}
              </template>
            </td>
            <td class="align-middle">
              {{ report.items_count }}
            </td>
            <td class="align-middle">
              {{ report.created_at }}
            </td>
            <td class="align-middle">
              {{ report.updated_at }}
            </td>
            <td class="align-middle">
              <d-button
                theme="primary"
                @click.prevent="openViewPopup(report.id)"
                class="mr-2"
                :title="$t('shipmentsReports.viewRelatedShipments')">
                <i class="material-icons">visibility</i>
              </d-button>
              <d-button
                theme="info"
                :disabled="!report.can_download_report || downloadingReport === report.id"
                @click.prevent="downloadReport(report.zip_file_url, report.id, report.date_from, report.date_to)"
                class="mr-2"
                :title="$t('shipmentsReports.downloadReport')">
                <template v-if="downloadingReport !== report.id">
                  <i class="material-icons">cloud_download</i>
                </template>
                <template v-else>
                  <div class="spinner-border spinner-border-sm" role="status"></div>
                </template>
              </d-button>
            </td>
          </tr>
          <tr v-if="reports.length === 0">
            <td
              colspan="8"
              class="text-center">
              {{ $t('shipmentsReports.noReportsToDisplay') }}&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div
        v-if="!allDataLoaded"
        class="pb-3 pt-3 text-center overlay-loading">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
      </d-alert>
    </d-card>

    <pagination
      :meta-data="paginationMetaData"
      :initial-page="currentPage" />

    <shipments-reports-view-popup
      v-if="showViewPopup"
      :reportID="selectedReportID" />
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import HasPagination from '../mixins/HasPagination.vue';
import FormUtils from '../utils/FormUtils.js';
import ListUtils from '../utils/ListUtils.js';
import Pagination from '../components/common/Pagination.vue';
import ShipmentsReportsViewPopup from '../components/shipments-reports/ShipmentsReportsView.vue';
import DateRangePicker from 'vue2-daterange-picker';
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css';
import Vue from 'vue';

export default {
  name: 'shipments-reports',
  mixins: [
    HasPagination
  ],
  components: {
    'date-range-picker': DateRangePicker,
    'pagination': Pagination,
    'shipments-reports-view-popup': ShipmentsReportsViewPopup
  },
  computed: {
    allDataLoaded () {
      return this.loaded;
    },
    statusBadgeTheme () {
      return {
        NEW: 'primary',
        GENERATING_POD: 'warning',
        MERGING_FILES: 'warning',
        ZIP_PACKING: 'warning',
        REMOVING_FILES: 'danger',
        EMAIL_SENDING: 'info',
        ENDED: 'success'
      }
    }
  },
  data() {
    let previousWeek = new Date();
    previousWeek.setDate(previousWeek.getDate() - 6);

    return {
      reports: [],
      loaded: false,
      loadError: false,
      showViewPopup: false,
      selectedReportID: null,
      generator: {
        type: 'ALL',
        date_range: {
          startDate: previousWeek,
          endDate: new Date()
        }
      },
      downloadingReport: false,
      generatingReportInProgress: false
    };
  },
  mounted () {
    this.loadFilteredData();
    this.$bus.$on('reports-reload', this.debouncedLoadFilteredData);
    this.$bus.$on('close-shipment-report-view-popup', this.closeReportViewPopup);
  },
  methods: {
    debouncedLoadFilteredData: debounce(function () {
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      ListUtils.loadPaginatedItemsData(this, {
        endpoint: '/shipments/reports/items?page=' + this.currentPage,
        listField: 'reports'
      });
    },
    generateReport () {
      this.generatingReportInProgress = true;

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/shipments/reports/generate',
        formData: {
          type: this.generator.type,
          date_from: this.prepareDateString(this.generator.date_range.startDate),
          date_to: this.prepareDateString(this.generator.date_range.endDate)
        },
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('shipmentsReports.errorDuringGeneratingReport'),
        successAction: () => {
          this.generatingReportInProgress = false;
          this.$bus.$emit('reports-reload');
        },
        failAction: () => {
          this.generatingReportInProgress = false;
        }
      });
    },
    closeReportViewPopup () {
      this.showViewPopup = false;
    },
    openViewPopup (reportID) {
      this.selectedReportID = reportID;
      this.showViewPopup = true;
    },
    formatDateStart (input) {
      if (input === null) {
        return this.$t('ui.chooseDate');
      }

      return [input.getFullYear(), input.getMonth() + 1, input.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    },
    formatDateEnd (input) {
      if (input === null) {
        return this.$t('ui.searchTo');
      }

      return [input.getFullYear(), input.getMonth() + 1, input.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    },
    prepareDateString (input) {
      return [input.getFullYear(), input.getMonth() + 1, input.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    },
    downloadReport (downloadUrl, reportID, dateFrom, dateTo) {
      this.downloadingReport = reportID;

      this.$http.post(downloadUrl, {}, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', dateFrom + '-' + dateTo + '.zip');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloadingReport = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccuredDuringDownload'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.downloadingReport = false;
      });
    }
  },
  beforeDestroy () {
    this.$bus.$off('reports-reload', this.debouncedLoadFilteredData);
    this.$bus.$off('close-shipment-report-view-popup', this.closeReportViewPopup);
  }
};
</script>
